const DAY_MS = 24 * 60 * 60 * 1000;

export const DEFAULT_PROFILE_FRESHNESS_POLICY = Object.freeze({
  freshMaxAgeDays: 7,
  staleAfterDays: 30,
});

const LEGACY_METRIC_PATHS = Object.freeze([
  "followers",
  "followerCount",
  "followers_count",
  "subscriberCount",
  "subscriber_count",
  "viewCount",
  "views",
  "media_count",
  "mediaCount",
  "avg_engagement_rate",
  "avg_engagement_rate_30d",
  "avgEngagementRate30d",
  "engagement_rate",
  "engagementRate",
  "posts_last_30d",
  "postsLast30d",
  "posts_last_90d",
  "postsLast90d",
  "posts_per_week_30d",
  "postsPerWeek30d",
  "activity_score",
  "activityScore",
  "last_post_at",
  "lastPostAt",
  "snapshot.followers",
  "snapshot.engagement_rate_30d",
  "snapshot.posts_last_30d",
]);

const firstValue = (obj, paths) => {
  for (const path of paths) {
    const value = path.split(".").reduce((current, key) => current?.[key], obj);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

const valueAtPath = (obj, path) =>
  path.split(".").reduce((current, key) => current?.[key], obj);

const hasMetricValue = (value) => {
  if (value === 0) return true;
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "number") return Number.isFinite(value);
  return String(value).trim().length > 0;
};

const normalizeTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const normalizeExplicitStatus = (value) => {
  const status = String(value || "").trim().toLowerCase();

  if (["fresh", "current", "verified"].includes(status)) return "fresh";
  if (["aging", "warning", "old"].includes(status)) return "aging";
  if (["legacy", "historical", "snapshot"].includes(status)) return "legacy";
  if (["stale", "expired", "outdated"].includes(status)) return "stale";
  if (["unknown", "unverified", "missing"].includes(status)) return "unknown";

  return null;
};

export const hasProfileMetricSnapshot = (profile) => {
  if (!profile || typeof profile !== "object") return false;

  if (LEGACY_METRIC_PATHS.some((path) => hasMetricValue(valueAtPath(profile, path)))) {
    return true;
  }

  return Array.isArray(profile.metrics)
    ? profile.metrics.some((metric) => hasMetricValue(metric?.value))
    : false;
};

export const getProfileMetricsObservedAt = (profile) =>
  normalizeTimestamp(
    firstValue(profile, [
      "data_quality.metrics_observed_at",
      "dataQuality.metricsObservedAt",
      "freshness.metrics_observed_at",
      "freshness.metricsObservedAt",
      "metrics_observed_at",
      "metricsObservedAt",
      "source_observed_at",
      "sourceObservedAt",
      "source_refreshed_at",
      "sourceRefreshedAt",
      "last_refreshed_at",
      "lastRefreshedAt",
    ])
  );

export const formatProfileObservedDate = (value, locale = "en-GB") => {
  const iso = normalizeTimestamp(value);
  if (!iso) return "";

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
};

export function evaluateProfileFreshness(
  profile,
  policy = DEFAULT_PROFILE_FRESHNESS_POLICY,
  now = Date.now()
) {
  const observedAt = getProfileMetricsObservedAt(profile);
  const hasLegacyMetrics = hasProfileMetricSnapshot(profile);
  const explicitStatus = normalizeExplicitStatus(
    firstValue(profile, [
      "data_quality.freshness_status",
      "dataQuality.freshnessStatus",
      "freshness.status",
      "freshness_status",
      "freshnessStatus",
    ])
  );

  const freshMaxAgeDays = Math.max(0, Number(policy?.freshMaxAgeDays ?? 7));
  const staleAfterDays = Math.max(
    freshMaxAgeDays,
    Number(policy?.staleAfterDays ?? 30)
  );

  let ageMs = null;
  let ageDays = null;

  if (observedAt) {
    ageMs = Math.max(0, Number(now) - new Date(observedAt).getTime());
    ageDays = ageMs / DAY_MS;
  }

  let status = explicitStatus;

  if (!observedAt) {
    // Compatibility state for pre-migration records. Existing values may be
    // displayed only as a clearly labelled historical snapshot. An explicit
    // stale state remains a hard block.
    status = explicitStatus === "stale"
      ? "stale"
      : hasLegacyMetrics
        ? "legacy"
        : "unknown";
  } else if (!status || status === "unknown" || status === "legacy") {
    if (ageDays <= freshMaxAgeDays) status = "fresh";
    else if (ageDays <= staleAfterDays) status = "aging";
    else status = "stale";
  }

  // A stale timestamp must not be made trustworthy by an incorrect explicit flag.
  if (observedAt && ageDays > staleAfterDays) status = "stale";

  const observedDateLabel = formatProfileObservedDate(observedAt);
  const canShowMetrics = ["fresh", "aging", "legacy"].includes(status);
  const canUseForDecision = status === "fresh" || status === "aging";
  const shouldWarn = status === "aging" || status === "legacy";

  const notice =
    status === "fresh"
      ? {
          title: "Stats verified",
          description: observedDateLabel ? `Checked ${observedDateLabel}` : "",
        }
      : status === "aging"
        ? {
            title: "Stats may be outdated",
            description: observedDateLabel
              ? `Checked ${observedDateLabel} · refresh recommended`
              : "Refresh recommended",
          }
        : status === "legacy"
          ? {
              title: "Historical snapshot",
              description: "Verification date unavailable · refresh recommended",
            }
          : status === "stale"
            ? {
                title: "Current stats unavailable",
                description: observedDateLabel
                  ? `Last verified ${observedDateLabel} · refresh required`
                  : "Refresh required",
              }
            : {
                title: "Current stats unavailable",
                description: "This creator has not yet been verified.",
              };

  const label = [notice.title, notice.description].filter(Boolean).join(" · ");

  return {
    status,
    observedAt,
    observedDateLabel,
    ageMs,
    ageDays,
    hasLegacyMetrics,
    canShowMetrics,
    canUseForDecision,
    canUseDerivedMetrics: canUseForDecision,
    shouldWarn,
    isFresh: status === "fresh",
    isAging: status === "aging",
    isLegacy: status === "legacy",
    isStale: status === "stale",
    isUnknown: status === "unknown",
    refreshRecommended: status !== "fresh",
    noticeTitle: notice.title,
    noticeDescription: notice.description,
    label,
  };
}

/**
 * Compact feed-card presentation. Cards are scanning surfaces, so only
 * actionable exceptions are displayed. Full warnings and refresh actions stay
 * on the creator detail page.
 */
export function getProfileCardFreshnessPresentation(
  profile,
  policy = DEFAULT_PROFILE_FRESHNESS_POLICY,
  now = Date.now()
) {
  const freshness = evaluateProfileFreshness(profile, policy, now);

  if (freshness.isFresh) {
    return {
      ...freshness,
      visible: true,
      tone: "fresh",
      label: freshness.observedDateLabel
        ? `Fresh metrics · checked ${freshness.observedDateLabel}`
        : "Fresh metrics",
      tooltip: freshness.observedDateLabel
        ? `Metrics are current. Checked ${freshness.observedDateLabel}.`
        : "Metrics are current.",
    };
  }

  if (freshness.isAging) {
    return {
      ...freshness,
      visible: true,
      tone: "aging",
      label: freshness.observedDateLabel
        ? `Checked ${freshness.observedDateLabel}`
        : "Stats may be outdated",
      tooltip: freshness.observedDateLabel
        ? `Stats may be outdated. Last checked ${freshness.observedDateLabel}. Open the profile to refresh.`
        : "Stats may be outdated. Open the profile to refresh.",
    };
  }

  if (freshness.isLegacy) {
    return {
      ...freshness,
      visible: true,
      tone: "legacy",
      label: "Historical metrics",
      tooltip:
        "Metrics are available, but their verification date is not recorded. Verify the profile before using these values for campaign decisions.",
    };
  }

  if (freshness.isStale) {
    return {
      ...freshness,
      visible: true,
      tone: "stale",
      label: "Stats outdated",
      tooltip: freshness.observedDateLabel
        ? `Last verified ${freshness.observedDateLabel}. Open the profile to refresh.`
        : "These statistics are outdated. Open the profile to refresh.",
    };
  }

  return {
    ...freshness,
    visible: true,
    tone: "unknown",
    label: "Stats unavailable",
    tooltip: "This creator's current statistics have not yet been verified.",
  };
}
